import React, { useState, useEffect, useMemo } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import SoftInput from "../SoftInput";
import SoftButton from "../SoftButton";
import { Divider, Switch, Button } from "@mui/material";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FORM_ELEMENTS from "./formElementOptions";

const DROPPABLE_SELECTED = "droppable_selected";
const DROPPABLE_OPTIONS = "droppable_options";
const NOTIFICATION_TYPE = 'notification_settings';

const KpiSubmissionFormBuilder = ({ errors, inputs, updateInputs }) => {
  const [itemIdx, setItemIdx] = useState(0);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const selectedElements = useMemo(() => {
    return inputs.filter(input => input.type !== NOTIFICATION_TYPE);
  }, [inputs]);
  const [formElements, setFormElements] = useState([...FORM_ELEMENTS]);

  const getFormOption = (draggable, element) => {
    return (
      <SoftBox
        ref={draggable?.innerRef}
        {...draggable?.draggableProps}
        {...draggable?.dragHandleProps}
        borderRadius="md"
        shadow="sm"
        sx={{
          cursor: element.disabled ? "stop" : "auto",
          border: "1px solid rgba(33, 82, 255, 0.25)",
          height: "2.25rem",
          width: "100%",
          px: 2,
          py: 2,
          background: element.disabled
            ? "rgba(15, 23, 42, 0.06)"
            : "rgba(15, 23, 42, 0.03)",
        }}
      >
        <SoftBox
          className="w-full h-full border-black-500 flex justify-between items-center"
          disabled={element.disabled}
        >
          <SoftTypography
            variant="button"
            disabled={true}
            sx={{ color: "#344054" }}
          >
            {element.label}
          </SoftTypography>
          {element.justOne ? (
            <SoftTypography
              variant="caption"
              disabled={true}
              sx={{ color: "#98A2B3" }}
            >
              (Max: 1)
            </SoftTypography>
          ) : null}
          {element.icon}
        </SoftBox>
      </SoftBox>
    );
  };

  const setFormElementOptions = (currentItems) => {
    // If limited item has already been selected, remove the item from the options by disabling it
    const limitedItems = currentItems
      .filter((item) => item.justOne)
      .map((item) => item.type);
    setFormElements(
      formElements.map((el) => ({
        ...el,
        ...(limitedItems.includes(el.id)
          ? { disabled: true }
          : { disabled: false }),
      })),
    );
  };

  useEffect(() => {
    if (errors && errors.inputs) {
      const filledUpForm = inputs.map((el, idx) => {
        const inputError =
          errors.inputs[idx]?.type?.message ||
          errors.inputs[idx]?.value?.message;
        return {
          ...el,
          ...(inputError && { error: inputError }),
        };
      });
      updateInputs?.(filledUpForm);
    }
  }, [errors]);

  useEffect(() => {
    const notificationSettings = inputs.find(input => input.type === NOTIFICATION_TYPE);
    if (notificationSettings?.value) {
      setNotificationEnabled(true);
    }
  }, []);

  const updateNotificationSettings = (enabled, email = '') => {
    const updatedElements = [...inputs].filter(input => input.type !== NOTIFICATION_TYPE);
    const notificationData = {
      id: `${NOTIFICATION_TYPE}-1`,
      type: NOTIFICATION_TYPE,
      enabled,
      value: email,
    };
    
    updatedElements.push(notificationData);
    updateInputs(updatedElements);
  };

  const updateValue = (id, value) => {
    const updatedElements = [...inputs];
    const elIdx = inputs.findIndex((el) => el.id === id);
    updatedElements[elIdx].value = value;
    updateInputs(updatedElements);
  };

  // Dropdown choices are typed comma-separated; the raw text is kept for
  // editing (so a trailing comma isn't eaten mid-keystroke) and the parsed
  // list is what gets saved with the metric.
  const updateOptions = (id, text) => {
    const updatedElements = [...inputs];
    const elIdx = inputs.findIndex((el) => el.id === id);
    updatedElements[elIdx].optionsText = text;
    updatedElements[elIdx].options = text
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    updateInputs(updatedElements);
  };

  const removeSelectedElement = (id) => {
    const updatedElements = [...inputs];
    const elIdx = inputs.findIndex((el) => el.id === id);
    const isLimited = inputs[elIdx].justOne;
    updatedElements.splice(elIdx, 1);
    updateInputs(updatedElements);

    if (isLimited) {
      // Set form elements options to re-enable previously locked limited form option
      setFormElementOptions(formElements);
    }
  };

  const getFormElement = (draggable, element, isDragging) => {
    return (
      <div className="flex flex-col gap-0">
        <SoftBox
          ref={draggable.innerRef}
          {...draggable.draggableProps}
          {...draggable.dragHandleProps}
          className="pb-1 mt-1 w-full flex gap-0"
          sx={{
            borderWidth: "0 0 0 0.25rem",
            borderColor: isDragging ? "primary.main" : "rgba(0,0,0,0)",
            background: isDragging
              ? "linear-gradient(310deg, rgba(12, 91, 219, 0.10) 0%, rgba(168, 184, 216, 0.10) 100%)"
              : "transparent",
            "&:hover": {
              background:
                "linear-gradient(310deg, rgba(12, 91, 219, 0.10) 0%, rgba(168, 184, 216, 0.10) 100%)",
              borderColor: "primary.main",
            },
          }}
        >
          <SoftBox
            className="grow border-1 rounded-md"
            sx={{
              borderColor: element.error
                ? "negative.lighter"
                : "rgb(203 213 225, 0.5)", // element.error? red: gray
            }}
          >
            <SoftInput
              size="medium"
              placeholder={`Enter ${element.label?.toLowerCase()} input label`}
              multiline
              icon={{
                component: element.icon,
                direction: "right",
              }}
              value={element.value}
              onChange={(e) => updateValue(element.id, e.target.value)}
              error={!!element.error}
              className="bg-transparent w-full !border-none"
              sx={{
                border: "0.65px solid #E4E7EC",
                borderColor: element.error
                  ? "rgba(0,0,0,0)"
                  : "#E4E7EC",
                backgroundColor: "#F6F7F9 !important",
                color: "#1d2939 !important",
                "& .MuiInputBase-input::placeholder": {
                  color: "#98A2B3 !important",
                  opacity: "1",
                },
                "&.Mui-focused": {
                  borderColor: element.error ? "rgba(0,0,0,0)" : "primary.main",
                },
              }}
            />
          </SoftBox>
          <SoftButton
            size="medium"
            iconOnly
            circular
            color="error"
            variant="text"
            onClick={() => removeSelectedElement(element.id)}
          >
            <CloseOutlinedIcon
              color="red"
              sx={{ width: "1.75rem", height: "auto" }}
            />
          </SoftButton>
        </SoftBox>
        {element.type === "dropdown" && (
          <SoftBox sx={{ pl: 1, pr: 6, pb: 1 }}>
            <SoftInput
              size="small"
              placeholder="Dropdown options, comma separated (e.g. Clean 365, Clean 365 VIP)"
              value={element.optionsText ?? (element.options || []).join(", ")}
              onChange={(e) => updateOptions(element.id, e.target.value)}
              sx={{
                border: "0.65px solid #E4E7EC",
                backgroundColor: "#F6F7F9 !important",
                color: "#1d2939 !important",
                fontSize: "0.8rem",
                "& .MuiInputBase-input::placeholder": {
                  color: "#98A2B3 !important",
                  opacity: "1",
                },
                "&.Mui-focused": { borderColor: "primary.main" },
              }}
            />
            {(element.options || []).length === 0 && (
              <p className="text-danger text-xs font-semibold px-1 pt-1">
                Add at least one option for this dropdown
              </p>
            )}
          </SoftBox>
        )}
        {element.error && (
          <p className="text-danger text-xs font-semibold px-4">
            {element.error}
          </p>
        )}
      </div>
    );
  };

  // Handle the element drop event
  const onDragEnd = (result) => {
    if (!result.destination) return;

    // Draggable was dropped on output form builder
    if (result.destination.droppableId === DROPPABLE_SELECTED) {
      // Draggable was taken from form element options: add selected item to output form
      if (result.source.droppableId === DROPPABLE_OPTIONS) {
        const draggedElement = formElements.find(
          (element) => element.id === result.draggableId,
        );
        const elemId = `${draggedElement.id}-${itemIdx + 1}`;
        const newSelected = [...selectedElements];
        newSelected.splice(result.destination.index, 0, {
          ...draggedElement,
          id: elemId,
          type: draggedElement.id,
          selected: true,
          value: "",
        });

        setItemIdx((prev) => prev + 1);
        updateInputs(newSelected);
        setFormElementOptions(newSelected);
      } else {
        // Draggable was taken from form output form: revise the order of the elements in the output form
        const revisedSelectedEls = [...selectedElements];
        const elIdx = revisedSelectedEls.findIndex(
          (el) => el.id === result.draggableId,
        );
        revisedSelectedEls.splice(elIdx, 1); // Remove selected from original position
        revisedSelectedEls.splice(
          result.destination.index,
          0,
          selectedElements[elIdx],
        ); // Then attach it to new position
        updateInputs(revisedSelectedEls);
      }
    } else if (result.source.droppableId === DROPPABLE_OPTIONS) {
      // Draggable was taken from and dropped on element options; not much to do here except re-order the form elements just for cosmetic reasons
      const revisedFormElements = [...formElements];
      const elIdx = revisedFormElements.findIndex(
        (el) => el.id === result.draggableId,
      );
      revisedFormElements.splice(elIdx, 1); // Remove selected from original position
      revisedFormElements.splice(
        result.destination.index,
        0,
        formElements[elIdx],
      ); // Then attach it to new position
      setFormElements(revisedFormElements);
    }
  };

  const notificationSection = (
    <SoftBox>
      <SoftBox display="flex" alignItems="flex-start" gap={2}>
        <SoftBox>
          <Switch 
            checked={notificationEnabled}
            onChange={(e) => {
              setNotificationEnabled(e.target.checked);
              updateNotificationSettings(e.target.checked, '');
            }}
          />
        </SoftBox>
        <SoftBox display="flex" flexDirection="column" gap={2}>
          <SoftTypography
            variant="h4"
            sx={{
              color: "#344054",
              fontSize: "13px",
              fontWeight: 400,
              marginTop: "0.35rem"
            }}
          >
            Send a notification for each submission
          </SoftTypography>
        </SoftBox>
      </SoftBox>
      {notificationEnabled && (
        <SoftBox>
          <SoftTypography sx={{ color: "#344054", fontSize: "12px", fontWeight: 400, marginTop: "0.75rem", marginBottom: "0.5rem" }}>
            Send notifications to this email
          </SoftTypography>
          <SoftInput
            value={inputs.find(input => input.type === NOTIFICATION_TYPE)?.value || ''}
            onChange={(e) => updateNotificationSettings(true, e.target.value)}
            sx={{
              border: "0.65px solid #E4E7EC",
              backgroundColor: "#F6F7F9 !important",
              color: "#1d2939 !important",
              "& .MuiInputBase-input::placeholder": {
                color: "#98A2B3 !important",
                opacity: "1",
              },
            }}
          />
        </SoftBox>
      )}
    </SoftBox>
  );

  return (
    <div className="w-fullflex flex-col justify-center">
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%" sx={{padding: "12px 24px"}}>
        <SoftTypography 
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontStyle: "normal",
          fontWeight: 600,
          fontSize: "14.699px",
          lineHeight: "19px",
          letterSpacing: "-0.02em",
          color: "#0F172A",
        }}>Build Your Submission Form</SoftTypography>
      </SoftBox>
      <svg width="100%" height="1" viewBox="0 0 531 1" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path 
          d="M-0.00976562 0.458984L531.01 0.458984" 
          stroke="url(#paint0_linear_694_11479)" 
          strokeOpacity="0.3" 
          strokeWidth="0.918685" 
          strokeLinejoin="round"
        />
        <defs>
          <linearGradient id="paint0_linear_694_11479" x1="-0.00980104" y1="-0.541016" x2="531.01" y2="-0.541016" gradientUnits="userSpaceOnUse">
            <stop stopColor="#36A2F0" stopOpacity="0.02" />
            <stop offset="0.5" stopColor="#36A2F0" />
            <stop offset="1" stopColor="#36A2F0" stopOpacity="0.02" />
          </linearGradient>
        </defs>
      </svg>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="elements-list rounded-xl h-full w-full flex gap-0">
          <Droppable
            droppableId={DROPPABLE_OPTIONS}
            type="ELEMENT"
            renderClone={(provided, _, rubric) => {
              const element = formElements[rubric.source.index];
              return getFormOption(provided, element);
            }}
          >
            {(provided, snapshot) => (
              <SoftBox
                shadow="md"
                className="flex flex-col gap-2 px-3 py-3"
                ref={provided.innerRef}
                sx={{
                  border: "2px solid rgba(15, 23, 42, 0.10)",
                  background: "rgba(15, 23, 42, 0.03)",
                  width: "200px",
                  height: "390px",
                  borderTopLeftRadius: "0px",
                  borderBottomLeftRadius: "7.34948px",
                  borderTopRightRadius: "0px",
                  borderBottomRightRadius: "0px",
                }}
                {...provided.droppableProps}
              >
                <SoftTypography
                  variant="body2"
                  sx={{
                    px: 2,
                    textAlign: "center",
                    mb: 2,
                    fontFamily: "'DM Sans', sans-serif",
                    fontStyle: "normal",
                    fontWeight: 600,
                    fontSize: "9.5px",
                    lineHeight: "12px",
                    letterSpacing: "-0.02em",
                    color: "#475467",
                  }}
                >
                  Drag and drop elements to the right to build your form
                </SoftTypography>
                {formElements.map((element, index) => {
                  const shouldRenderClone =
                    element.id === snapshot.draggingFromThisWith;

                  return shouldRenderClone ? (
                    // Render element clone without draggable properties
                    getFormOption(null, element)
                  ) : (
                    // Render draggable element
                    <Draggable
                      key={element.id}
                      draggableId={element.id}
                      index={index}
                      isDragDisabled={element.disabled}
                    >
                      {(draggable) => getFormOption(draggable, element)}
                    </Draggable>
                  );
                })}
                {errors && errors.inputs && (
                  <p className="text-danger text-xs font-semibold">
                    {errors.inputs.message}
                  </p>
                )}
                {provided.placeholder}
              </SoftBox>
            )}
          </Droppable>
          <Droppable
            droppableId={DROPPABLE_SELECTED}
            type="ELEMENT"
            renderClone={(provided, _, rubric) => {
              const element = selectedElements[rubric.source.index];
              return getFormElement(provided, element, true);
            }}
          >
            {(provided) => (
              <SoftBox
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-col gap-1 px-3 py-2 grow"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%"
                }}
              >
                <SoftBox
                  sx={{
                    background: "rgba(15, 23, 42, 0.03)",
                    border: "0.841521px dashed rgba(15, 23, 42, 0.10)",
                    borderRadius: "6.73217px",
                    minHeight: "208px",
                    padding: "16px"
                  }}
                >
                  {selectedElements.map((element, index) => (
                    <Draggable
                      key={element.id}
                      draggableId={element.id}
                      index={index}
                    >
                      {(draggable) => getFormElement(draggable, element)}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {selectedElements.length === 0 && (
                    <SoftBox 
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      flexGrow: 1,
                      marginTop: "64px"
                    }}>
                    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" style={{margin: "0 auto!important"}}>
                      <g filter="url(#filter0_d_694_11548)">
                        <circle cx="19" cy="15" r="15" fill="#0F172A" fillOpacity="0.03" shapeRendering="crispEdges"/>
                        <circle cx="19" cy="15" r="14.5" stroke="#0F172A" strokeOpacity="0.1" shapeRendering="crispEdges"/>
                      </g>
                      <path d="M23.6673 15.6654H19.6673V19.6654H18.334V15.6654H14.334V14.332H18.334V10.332H19.6673V14.332H23.6673V15.6654Z" fill="#98A2B3"/>
                      <defs>
                        <filter id="filter0_d_694_11548" x="0" y="0" width="38" height="38" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                          <feOffset dy="4"/>
                          <feGaussianBlur stdDeviation="2"/>
                          <feComposite in2="hardAlpha" operator="out"/>
                          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_694_11548"/>
                          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_694_11548" result="shape"/>
                        </filter>
                      </defs>
                    </svg>
                    <SoftTypography sx={{ fontFamily: 'DM Sans', fontStyle: 'normal', fontWeight: 400, color: '#475467', textAlign: 'center', fontSize: "9.5px", lineHeight: "12px", letterSpacing: "-0.02em", }}>
                      Drag elements here to build form
                    </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
                <SoftBox mt={2}>
                  {notificationSection}
                </SoftBox>
              </SoftBox>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
  );
};

export default KpiSubmissionFormBuilder;
